/**
 * Calcul TURPE par PDL — fonction PURE extraite de Step6Validation.jsx.
 *
 * ⚠️ IMPORTANT : cette logique doit rester STRICTEMENT IDENTIQUE au calcul inline
 * de `Step6Validation.jsx` (affichage des cartes). Elle sert à recalculer les
 * économies de TOUS les PDL au moment de la génération du rapport (Step7), sans
 * dépendre du rendu paginé de l'étape 6 (qui ne monte que 10 cartes à la fois et
 * ne remplissait donc `calculsParPDL` que pour les PDL réellement affichés → les
 * autres PDL sortaient à 0 dans le PDF/rapport pour les gros dossiers ~200 PDL).
 *
 * @param {string} pdl - numéro de PDL
 * @param {Object} resultatPDL - { tousLesPoints, puissancesMaxParMois, donneesDegradees, depassementsStockes, ... }
 * @param {Object} deps - dépendances injectées (valeurs, pas de refs) :
 *   { formData, preconisationsPuissance, preconisationsPuissanceParPlage,
 *     consommationsParPlage, cerOverride, determinerPlageHoraire, calculerConsommationsParPlage }
 * @returns {Object|null} nouveauxCalculs (même forme que Step6) ou null si pas de données
 */

// ========== TARIFS TURPE 7 PAR TENSION (01/08/2025) ==========
const TARIFS_PAR_TENSION = {
  HTA: { CG: { CARD: 499.80, Unique: 435.72 }, CC: 376.39, CER: 2.44, CMDPS_ALPHA: 0.04, unite: 'kW' },
  BTSUP: { CG: { CARD: 249.84, Unique: 217.80 }, CC: 283.27, CER: 0, CMDPS_H: 12.41, unite: 'kVA' },
  BTINF: { CG: { CARD: 18.00, Unique: 16.80 }, CC: 22.00, CER: 0, CMDPS_H: 0, unite: 'kVA' },
};

// Coefficients par formule tarifaire
const COEFFICIENTS_PAR_FORMULE = {
  'HTACU5 PF': { plages: ['Pointe', 'HPH', 'HCH', 'HPB', 'HCB'], b: { Pointe: 14.41, HPH: 14.41, HCH: 14.41, HPB: 12.55, HCB: 11.22 }, c: { Pointe: 0.0574, HPH: 0.0423, HCH: 0.0199, HPB: 0.0101, HCB: 0.0069 } },
  'HTACU5 PM': { plages: ['Pointe', 'HPH', 'HCH', 'HPB', 'HCB'], b: { Pointe: 14.41, HPH: 14.41, HCH: 14.41, HPB: 12.55, HCB: 11.22 }, c: { Pointe: 0.0701, HPH: 0.0405, HCH: 0.0199, HPB: 0.0101, HCB: 0.0069 } },
  'HTALU5 PF': { plages: ['Pointe', 'HPH', 'HCH', 'HPB', 'HCB'], b: { Pointe: 35.33, HPH: 32.30, HCH: 20.39, HPB: 14.33, HCB: 11.56 }, c: { Pointe: 0.0265, HPH: 0.0210, HCH: 0.0147, HPB: 0.0092, HCB: 0.0068 } },
  'HTALU5 PM': { plages: ['Pointe', 'HPH', 'HCH', 'HPB', 'HCB'], b: { Pointe: 38.27, HPH: 34.30, HCH: 20.39, HPB: 14.33, HCB: 11.56 }, c: { Pointe: 0.0315, HPH: 0.0187, HCH: 0.0147, HPB: 0.0092, HCB: 0.0068 } },
  'BTSUPCU4': { plages: ['HPH', 'HCH', 'HPB', 'HCB'], b: { HPH: 17.61, HCH: 15.96, HPB: 14.56, HCB: 11.98 }, c: { HPH: 0.0691, HCH: 0.0421, HPB: 0.0213, HCB: 0.0152 } },
  'BTSUPLU4': { plages: ['HPH', 'HCH', 'HPB', 'HCB'], b: { HPH: 30.16, HCH: 21.18, HPB: 16.64, HCB: 12.37 }, c: { HPH: 0.0569, HCH: 0.0347, HPB: 0.0201, HCB: 0.0149 } },
  'BTINFCU4': { plages: ['HPH', 'HCH', 'HPB', 'HCB'], b: { HPH: 10.11, HCH: 10.11, HPB: 10.11, HCB: 10.11 }, c: { HPH: 0.0749, HCH: 0.0397, HPB: 0.0166, HCB: 0.0116 } },
  'BTINFMU4': { plages: ['HPH', 'HCH', 'HPB', 'HCB'], b: { HPH: 12.12, HCH: 12.12, HPB: 12.12, HCB: 12.12 }, c: { HPH: 0.0700, HCH: 0.0373, HPB: 0.0161, HCB: 0.0111 } },
  'BTINFMUDT': { plages: ['HP', 'HC'], b: { HP: 13.49, HC: 13.49 }, c: { HP: 0.0494, HC: 0.0350 } },
  'BTINFLU': { plages: ['Unique'], b: { Unique: 93.13 }, c: { Unique: 0.0125 } },
  'BTINFCU': { plages: ['Unique'], b: { Unique: 11.07 }, c: { Unique: 0.0484 } },
};

// Applique la saisie manuelle CMDPS ({ actuel?, propose? } en €) sur les valeurs calculées.
export const appliquerOverrideCMDPS = (ov, calcActuel, calcPropose) => {
  const val = (x, d) => (x !== undefined && x !== null && x !== '' && !isNaN(parseFloat(x)) ? parseFloat(x) : d);
  return { actuel: val(ov?.actuel, calcActuel), propose: val(ov?.propose, calcPropose) };
};

export function computeCalculsPDL(pdl, resultatPDL, deps) {
  const {
    formData,
    preconisationsPuissance = {},
    preconisationsPuissanceParPlage = {},
    consommationsParPlage = {},
    cerOverride = {},
    cmdpsOverride = {},
    determinerPlageHoraire,
    calculerConsommationsParPlage,
  } = deps || {};

  if (!resultatPDL) return null;
  const datasMois = resultatPDL.puissancesMaxParMois || [];
  const tousLesPoints = resultatPDL.tousLesPoints || [];
  if (datasMois.length === 0) return null;

  const pdlConfig = formData.pdls.find((p) => p.pdl === pdl);
  const puissancesActuellesParPlage = pdlConfig?.puissances_actuelles || {};
  const puissanceSouscrite = Math.max(
    puissancesActuellesParPlage?.Unique || 0,
    puissancesActuellesParPlage?.HP || 0,
    puissancesActuellesParPlage?.HC || 0,
    puissancesActuellesParPlage?.Pointe || 0,
    puissancesActuellesParPlage?.HPH || 0,
    puissancesActuellesParPlage?.HCH || 0,
    puissancesActuellesParPlage?.HPB || 0,
    puissancesActuellesParPlage?.HCB || 0,
    puissancesActuellesParPlage?.max || 0
  );
  const puissanceProposee = preconisationsPuissance[pdl] || 0;
  const puissancesPreconiseesParPlage = preconisationsPuissanceParPlage[pdl] || {};
  const formuleActuelle = pdlConfig?.formule_actuelle || '';
  const tension = pdlConfig?.tension || 'HTA';
  const contrat = pdlConfig?.contrat || 'CARD';

  const formuleSimulation = preconisationsPuissance[`${pdl}_formule`] || formuleActuelle;

  const getTensionFromFormule = (formule) => {
    if (formule.startsWith('HTA')) return 'HTA';
    if (formule.startsWith('BTSUP')) return 'BTSUP';
    if (formule.startsWith('BTINF')) return 'BTINF';
    return tension;
  };

  const tensionActuelle = getTensionFromFormule(formuleActuelle);
  const tensionSimulation = getTensionFromFormule(formuleSimulation);

  const tarifsActuels = TARIFS_PAR_TENSION[tensionActuelle] || TARIFS_PAR_TENSION.HTA;
  const tarifsSimulation = TARIFS_PAR_TENSION[tensionSimulation] || TARIFS_PAR_TENSION.HTA;

  const coeffsActuels = COEFFICIENTS_PAR_FORMULE[formuleActuelle] || COEFFICIENTS_PAR_FORMULE['HTACU5 PF'];
  const coeffsSimulation = COEFFICIENTS_PAR_FORMULE[formuleSimulation] || coeffsActuels;

  const CTA_TAUX = 0.15;

  // Consommation totale (kWh) - extrapolé sur 1 an
  const nbJoursData = new Set(tousLesPoints.map((p) => {
    const d = new Date(p.horodate);
    return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  })).size || 1;
  const facteurAnnualisation = 365 / nbJoursData;

  const consoTotaleKWh = tousLesPoints.reduce((sum, p) => sum + (p.kW || 0), 0);

  // Consommations par plage : priorité aux valeurs saisies/éditées, sinon calcul depuis les points.
  let consoParPlage = consommationsParPlage[pdl];
  if (!consoParPlage || Object.keys(consoParPlage).length === 0) {
    consoParPlage = tousLesPoints.length > 0
      ? calculerConsommationsParPlage(tousLesPoints, formuleActuelle, pdl)
      : {};
  }
  const totalConsoParPlage = Object.values(consoParPlage).reduce((sum, v) => sum + (v || 0), 0);
  const consoAnnuelleKWh = totalConsoParPlage > 0 ? totalConsoParPlage : consoTotaleKWh;

  // Énergie réactive totale (info)
  const energieReactiveTotale = tousLesPoints.reduce((sum, p) => {
    const kVA = p.kVA || 0;
    const kW = p.kW || 0;
    const kVAR = Math.sqrt(Math.max(0, kVA * kVA - kW * kW));
    return sum + kVAR;
  }, 0);
  const tanPhiMoyen = consoTotaleKWh > 0 ? energieReactiveTotale / consoTotaleKWh : 0;

  // CER : énergie réactive facturable (HTA) sur plages HPH (Pointe + HPH)
  const TAN_PHI_MAX = 0.40;
  const { consoActiveHPH, consoReactiveHPH } = tousLesPoints.reduce((acc, p) => {
    const plage = determinerPlageHoraire(p.horodate, formuleActuelle, pdl);
    if (plage === 'Pointe' || plage === 'HPH') {
      const kVA = p.kVA || 0;
      const kW = p.kW || 0;
      const kVAR = Math.sqrt(Math.max(0, kVA * kVA - kW * kW));
      acc.consoActiveHPH += kW / 6;
      acc.consoReactiveHPH += kVAR / 6;
    }
    return acc;
  }, { consoActiveHPH: 0, consoReactiveHPH: 0 });

  const consoActiveHPHAnnuelle = consoActiveHPH * facteurAnnualisation;
  const consoReactiveHPHAnnuelle = consoReactiveHPH * facteurAnnualisation;
  const tanPhiHPH = consoActiveHPHAnnuelle > 0 ? consoReactiveHPHAnnuelle / consoActiveHPHAnnuelle : 0;
  const energieReactiveFacturable = tanPhiHPH > TAN_PHI_MAX
    ? Math.max(0, consoReactiveHPHAnnuelle - (consoActiveHPHAnnuelle * TAN_PHI_MAX))
    : 0;

  // Dépassements avancés (PS par plage)
  const calculerDepassementsAvances = (puissances, coeffs, formule, tensionType) => {
    const plagesConnues = coeffs?.plages || ['Pointe', 'HPH', 'HCH', 'HPB', 'HCB', 'HP', 'HC', 'Unique'];
    let puissancesParPlage;
    if (typeof puissances === 'number') {
      if (puissances <= 0) return { nbPoints: 0, heuresUniques: 0, sommeBDeltaCarre: 0 };
      puissancesParPlage = Object.fromEntries(plagesConnues.map((p) => [p, puissances]));
    } else {
      puissancesParPlage = puissances || {};
      const hasAny = Object.values(puissancesParPlage).some((v) => (Number(v) || 0) > 0);
      if (!hasAny) return { nbPoints: 0, heuresUniques: 0, sommeBDeltaCarre: 0 };
    }

    let sommeBDeltaCarre = 0;
    const sommeDeltaCarreParPlage = {};
    const heuresAvecDepassement = new Set();
    let nbPoints = 0;

    tousLesPoints.forEach((p) => {
      const plage = determinerPlageHoraire(p.horodate, formule, pdl);
      const psPlage = Number(puissancesParPlage[plage]) || 0;
      if (psPlage <= 0) return;
      const valeur = tensionType === 'HTA' ? (p.kW || 0) : (p.kVA || 0);
      if (valeur <= psPlage) return;
      nbPoints += 1;
      const delta = valeur - psPlage;
      const b = coeffs?.b?.[plage] || 0;
      sommeBDeltaCarre += b * (delta * delta);
      sommeDeltaCarreParPlage[plage] = (sommeDeltaCarreParPlage[plage] || 0) + delta * delta;
      const d = new Date(p.horodate);
      const heureKey = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}-${d.getHours()}`;
      heuresAvecDepassement.add(heureKey);
    });

    return { nbPoints, heuresUniques: heuresAvecDepassement.size, sommeBDeltaCarre, sommeDeltaCarreParPlage };
  };

  const depActuel = calculerDepassementsAvances(puissancesActuellesParPlage, coeffsActuels, formuleActuelle, tensionActuelle);
  const puissancesPourDepPropose = (puissancesPreconiseesParPlage && Object.keys(puissancesPreconiseesParPlage).length > 0)
    ? puissancesPreconiseesParPlage
    : puissanceProposee;
  const depPropose = calculerDepassementsAvances(puissancesPourDepPropose, coeffsSimulation, formuleSimulation, tensionSimulation);

  // Réutilisation des agrégats natifs de dépassement (données rechargées dégradées)
  if (resultatPDL.donneesDegradees && resultatPDL.depassementsStockes) {
    const memePuissances = (a, b, plages) => {
      if (!a || !b || typeof a !== 'object' || typeof b !== 'object') return false;
      const liste = (plages && plages.length) ? plages : Object.keys(a);
      let auMoinsUne = false;
      for (const k of liste) {
        const va = Number(a[k] || 0);
        const vb = Number(b[k] || 0);
        if (va > 0 || vb > 0) auMoinsUne = true;
        if (va !== vb) return false;
      }
      return auMoinsUne;
    };
    const dep = resultatPDL.depassementsStockes;
    if (dep.actuel?.sommeDeltaCarreParPlage
        && memePuissances(puissancesActuellesParPlage, resultatPDL.puissancesActuellesStockees, coeffsActuels?.plages)) {
      depActuel.sommeDeltaCarreParPlage = dep.actuel.sommeDeltaCarreParPlage;
      if (dep.actuel.nbPoints != null) depActuel.nbPoints = dep.actuel.nbPoints;
      if (dep.actuel.heuresUniques != null) depActuel.heuresUniques = dep.actuel.heuresUniques;
    }
    if (dep.propose?.sommeDeltaCarreParPlage
        && memePuissances(puissancesPourDepPropose, resultatPDL.puissancesProposeesStockees, coeffsSimulation?.plages)) {
      depPropose.sommeDeltaCarreParPlage = dep.propose.sommeDeltaCarreParPlage;
      if (dep.propose.nbPoints != null) depPropose.nbPoints = dep.propose.nbPoints;
      if (dep.propose.heuresUniques != null) depPropose.heuresUniques = dep.propose.heuresUniques;
    }
  }

  // ===== Composantes en € =====
  const CG_Actuel = tarifsActuels.CG[contrat] || tarifsActuels.CG.CARD;
  const CG_Propose = tarifsSimulation.CG[contrat] || tarifsSimulation.CG.CARD;
  const CC_Actuel = tarifsActuels.CC;
  const CC_Propose = tarifsSimulation.CC;

  const calculerCSFixe = (puissances, coeffs) => {
    if (!coeffs || !coeffs.plages || coeffs.plages.length === 0) return 0;
    const plages = coeffs.plages;
    let total = 0;
    const parPlage = typeof puissances === 'number'
      ? Object.fromEntries(plages.map((p) => [p, puissances]))
      : puissances;
    const premierePlage = plages[0];
    const bPremiere = coeffs.b[premierePlage] || 0;
    const pPremiere = parPlage[premierePlage] || 0;
    total += bPremiere * pPremiere;
    for (let i = 1; i < plages.length; i++) {
      const plage = plages[i];
      const plagePrecedente = plages[i - 1];
      const b = coeffs.b[plage] || 0;
      const p = parPlage[plage] || 0;
      const pPrec = parPlage[plagePrecedente] || 0;
      const delta = Math.max(0, p - pPrec);
      total += b * delta;
    }
    return total;
  };

  const puissancesActuellesObj = pdlConfig?.puissances_actuelles || {};
  const puissancesActuellesPourCalcul = {};
  (coeffsActuels?.plages || []).forEach((plage) => {
    puissancesActuellesPourCalcul[plage] = puissancesActuellesObj[plage] || puissanceSouscrite;
  });
  const puissancesProposesPourCalcul = {};
  (coeffsSimulation?.plages || []).forEach((plage) => {
    puissancesProposesPourCalcul[plage] = puissancesPreconiseesParPlage[plage] || puissanceProposee || puissanceSouscrite;
  });
  const CSFixeActuel = calculerCSFixe(puissancesActuellesPourCalcul, coeffsActuels);
  const CSFixePropose = calculerCSFixe(puissancesProposesPourCalcul, coeffsSimulation);

  const calculerCSVariable = (coeffs) => {
    if (!coeffs) return 0;
    let total = 0;
    coeffs.plages.forEach((plage) => {
      const c = coeffs.c[plage] || 0;
      const conso = consoParPlage[plage] || 0;
      total += c * conso;
    });
    return total;
  };
  const CSVariableActuel = calculerCSVariable(coeffsActuels);
  const CSVariablePropose = calculerCSVariable(coeffsSimulation);

  const CTAActuel = CTA_TAUX * (CG_Actuel + CC_Actuel + CSFixeActuel);
  const CTAPropose = CTA_TAUX * (CG_Propose + CC_Propose + CSFixePropose);

  const CER_Actuel_Calcule = (tensionActuelle === 'HTA' && tarifsActuels.CER > 0)
    ? energieReactiveFacturable * tarifsActuels.CER / 100
    : 0;
  const CER_Propose_Calcule = (tensionSimulation === 'HTA' && tarifsSimulation.CER > 0)
    ? energieReactiveFacturable * tarifsSimulation.CER / 100
    : 0;
  const cerOv = cerOverride[pdl] || {};
  const CER_Actuel = cerOv.actuel !== undefined && cerOv.actuel !== '' ? parseFloat(cerOv.actuel) : CER_Actuel_Calcule;
  const CER_Propose = cerOv.propose !== undefined && cerOv.propose !== '' ? parseFloat(cerOv.propose) : CER_Propose_Calcule;

  const calculerCMDPS = (dep, tarifs, coeffs) => {
    if (tarifs.CMDPS_ALPHA && dep.sommeDeltaCarreParPlage) {
      const alpha = tarifs.CMDPS_ALPHA;
      let total = 0;
      Object.entries(dep.sommeDeltaCarreParPlage).forEach(([plage, sommeDP2]) => {
        if (sommeDP2 > 0) {
          const b = coeffs?.b?.[plage] || 0;
          total += alpha * b * Math.sqrt(sommeDP2);
        }
      });
      return total;
    } else if (tarifs.CMDPS_H && dep.heuresUniques > 0) {
      const heuresAnnuelles = dep.heuresUniques * facteurAnnualisation;
      return tarifs.CMDPS_H * heuresAnnuelles;
    }
    return 0;
  };
  const CMDPSActuelCalcule = calculerCMDPS(depActuel, tarifsActuels, coeffsActuels);
  const CMDPSProposeCalcule = calculerCMDPS(depPropose, tarifsSimulation, coeffsSimulation);
  const cmdpsOv = cmdpsOverride[pdl] || null;
  const { actuel: CMDPSActuel, propose: CMDPSPropose } = appliquerOverrideCMDPS(cmdpsOv, CMDPSActuelCalcule, CMDPSProposeCalcule);

  const totalActuel = CG_Actuel + CC_Actuel + CSFixeActuel + CSVariableActuel + CTAActuel + CER_Actuel + CMDPSActuel;
  const totalPropose = CG_Propose + CC_Propose + CSFixePropose + CSVariablePropose + CTAPropose + CER_Propose + CMDPSPropose;
  const economie = totalActuel - totalPropose;

  const puissancesActuelles = pdlConfig?.puissances_actuelles || {};
  const puissancesProposees = {};
  const plagesFormule = coeffsActuels?.plages || ['Pointe', 'HPH', 'HCH', 'HPB', 'HCB'];
  plagesFormule.forEach((plage) => {
    puissancesProposees[plage] = puissancesPreconiseesParPlage[plage] || puissanceProposee || puissanceSouscrite;
  });

  return {
    pdl,
    tensionActuelle,
    formuleActuelle,
    formuleProposee: formuleSimulation,
    contrat,
    puissanceSouscrite,
    puissanceProposee,
    puissancePreconisee: puissanceProposee,
    puissancesActuelles,
    puissancesProposees,
    puissancesPreconiseesParPlage,
    nbJoursData,
    facteurAnnualisation,
    consoAnnuelleKWh,
    tanPhiMoyen,
    tanPhiHPH,
    energieReactiveFacturable,
    totalActuel,
    totalPropose,
    economie,
    composantes: {
      CG: { actuel: CG_Actuel, propose: CG_Propose },
      CC: { actuel: CC_Actuel, propose: CC_Propose },
      'CS Fixe': { actuel: CSFixeActuel, propose: CSFixePropose },
      'CS Variable': { actuel: CSVariableActuel, propose: CSVariablePropose },
      CTA: { actuel: CTAActuel, propose: CTAPropose },
      CER: { actuel: CER_Actuel, propose: CER_Propose },
      CMDPS: { actuel: CMDPSActuel, propose: CMDPSPropose },
    },
    consommations: consoParPlage,
    depassements: { actuel: depActuel, propose: depPropose },
    cmdpsOverride: cmdpsOv,
    cmdpsCalcule: { actuel: CMDPSActuelCalcule, propose: CMDPSProposeCalcule },
  };
}
